// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import checkoutSharedStyles from "@conklin/App/SharedStyles/CheckoutStyles";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@conklin/Overrides/Components/AddressInfoDisplay";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        shipTo: getShipToState(state, cart?.shipToId)?.value,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationBillingAndShippingStyles {
    container?: GridContainerProps;
    shipToGridItem?: GridItemProps;
    shipToTitleText?: TypographyPresentationProps;
    shipToText?: TypographyPresentationProps;
    shipToAddress?: AddressInfoDisplayStyles;
    pickUpGridItem?: GridItemProps;
    pickUpTitleText?: TypographyPresentationProps;
    pickUpText?: TypographyPresentationProps;
    pickUpAddress?: AddressInfoDisplayStyles;
    billToGridItem?: GridItemProps;
    billToTitleText?: TypographyPresentationProps;
    billToText?: TypographyPresentationProps;
    // XNG-Changes
    section?: InjectableCss;
    deliveryMethodGridItem?: GridItemProps;
    deliveryMethodTitleText?: TypographyPresentationProps;
    deliveryMethodText?: TypographyPresentationProps;
    notesSection?: InjectableCss;
    notesTitleText?: TypographyPresentationProps;
    notesText?: TypographyPresentationProps;
}

export const billingAndShippingStyles: OnePageCheckoutConfirmationBillingAndShippingStyles = {
    container: { gap: 20 },
    shipToGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    shipToTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    pickUpGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    pickUpTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    billToGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    billToTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = billingAndShippingStyles;

const OnePageCheckoutConfirmationBillingAndShipping = ({ cart, billTo, shipTo }: Props) => {
    if (!cart || !billTo || !shipTo) {
        return null;
    }

    // XNG-Changes: structure
    return (
        <>
            <Typography {...checkoutSharedStyles.containerTitle}>{translate("Addresses")}</Typography>
            <StyledWrapper {...checkoutSharedStyles.container}>
                <StyledWrapper {...checkoutSharedStyles.section}>
                    <Typography as="h3" {...checkoutSharedStyles.sectionTitle}>
                        {translate("Billing Address")}
                    </Typography>
                    <Typography as="p" {...styles.billToText}>
                        <AddressInfoDisplay
                            {...billTo}
                            state={billTo.state?.abbreviation}
                            country={billTo.country?.name}
                            // XNG-Changes
                            attention=""
                            displayCustomerNumber={false}
                        />
                    </Typography>
                </StyledWrapper>
                {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                    <StyledWrapper
                        {...checkoutSharedStyles.section}
                        data-test-selector="onePageCheckOutConfirmationShippingInformation"
                    >
                        <Typography as="h3" {...checkoutSharedStyles.sectionTitle}>
                            {translate("Shipping Address")}
                        </Typography>
                        <AddressInfoDisplay
                            {...shipTo}
                            state={shipTo.state?.abbreviation}
                            country={shipTo.country?.name}
                            extendedStyles={styles.shipToAddress}
                            displayCustomerNumber={false}
                        />
                    </StyledWrapper>
                )}
                {cart.fulfillmentMethod === FulfillmentMethod.PickUp && cart.defaultWarehouse && (
                    <StyledWrapper
                        {...checkoutSharedStyles.section}
                        data-test-selector="onePageCheckOutConfirmationPickupInformation"
                    >
                        <Typography as="h3" {...checkoutSharedStyles.sectionTitle}>
                            {/* XNG-Changes */}
                            {translate("Pick Up Address")}
                        </Typography>
                        <AddressInfoDisplay
                            {...cart.defaultWarehouse}
                            companyName={cart.defaultWarehouse?.description || cart.defaultWarehouse?.name}
                            extendedStyles={styles.pickUpAddress}
                            displayCustomerNumber={false}
                        />
                    </StyledWrapper>
                )}

                {cart.notes && (
                    <StyledWrapper {...styles.notesSection}>
                        <Typography
                            {...checkoutSharedStyles.sectionTitle}
                            // XNG-Changes: data-test-selector
                            data-test-selector="orderConfirmation_orderNotes_title"
                        >
                            {translate("Order Notes")}
                        </Typography>
                        <Typography
                            {...styles.notesText}
                            // XNG-Changes: data-test-selector
                            data-test-selector="orderConfirmation_orderNotes_text"
                        >
                            {cart.notes}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationBillingAndShipping),
    definition: {
        displayName: "Billing And Shipping",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
